import { v4 as uuid } from 'uuid';

/** Crew requirement metadata — same shape as the custom block dialog uses. */
export interface BlockCrewRequirement {
    crewLeads: number;
    crewMembers: number;
    opsPeople: number;
}

/**
 * A single entry in the `calendarEvents.defaultBlocks` zone config.
 * Describes a block that is automatically created on the dispatch page
 * for each day a dispatcher opens.
 */
export interface DefaultBlockConfig {
    /** Block type — one of the special dispatch block event types. */
    blockType: 'ops-manager' | 'reserve' | 'non-billable';
    /** Display name of the block (e.g. "Ops Manager", "Training at the office"). */
    name: string;
    /** Start hour of the day (0-23). Blocks are created in the browser's local day. */
    startHour: number;
    /** End hour of the day (0-24, may cross midnight relative to startHour). */
    endHour: number;
    /** Optional crew requirement metadata, same shape as the custom block dialog. */
    crewRequirement?: BlockCrewRequirement;
}

/**
 * Structural slice of a calendar event used for default-block matching.
 * Accepts any generated fragment shape (dispatch, schedule, …) without
 * pinning to one.
 */
export interface BlockMatchEvent {
    type?: string | null;
    title?: string | null;
    start?: number | null;
    end?: number | null;
    metadata?: any;
}

/** Metadata key set on events created from config defaults. */
export const AUTO_GENERATED_META_KEY = 'autoGenerated';

/** Value stored under AUTO_GENERATED_META_KEY. */
export const AUTO_GENERATED_META_VALUE = 'defaultBlocks';

/** Valid block types for config entries. */
export const VALID_BLOCK_TYPES = ['ops-manager', 'reserve', 'non-billable'] as const;

export type BlockType = (typeof VALID_BLOCK_TYPES)[number];

/** Whether an event type is one of the special dispatch block types. */
export function isBlockType(type: string | null | undefined): type is BlockType {
    return !!type && (VALID_BLOCK_TYPES as readonly string[]).includes(type);
}

/**
 * Parse and validate a raw config value (JSON string, array, or null)
 * into DefaultBlockConfig entries. Invalid entries are dropped rather than
 * throwing — a bad config entry should never break the dispatch page load.
 */
export function parseDefaultBlocksConfig(raw: string | object[] | null | undefined): DefaultBlockConfig[] {
    if (!raw) return [];

    let entries: any[];
    if (typeof raw === 'string') {
        try {
            entries = JSON.parse(raw);
        } catch {
            return [];
        }
    } else if (Array.isArray(raw)) {
        entries = raw;
    } else {
        return [];
    }

    if (!Array.isArray(entries)) return [];

    return entries.filter((entry): entry is DefaultBlockConfig => {
        if (!entry || typeof entry !== 'object') return false;
        if (!VALID_BLOCK_TYPES.includes(entry.blockType)) return false;
        if (typeof entry.name !== 'string' || !entry.name.trim()) return false;
        if (typeof entry.startHour !== 'number' || !Number.isFinite(entry.startHour)) return false;
        if (typeof entry.endHour !== 'number' || !Number.isFinite(entry.endHour)) return false;
        if (entry.startHour < 0 || entry.startHour > 23) return false;
        if (entry.endHour < 0 || entry.endHour > 24) return false;
        return true;
    });
}

/**
 * Whether an event was auto-created from the default blocks config.
 * Checks the metadata flag written at creation time. Tolerates metadata
 * stored as a JSON string (Neo4j round-trip) or a parsed object.
 */
export function isAutoGeneratedBlock(event: BlockMatchEvent | { metadata?: any }): boolean {
    const metadata = event?.metadata;
    if (!metadata) return false;

    let parsed: any = metadata;
    if (typeof metadata === 'string') {
        try {
            parsed = JSON.parse(metadata);
        } catch {
            return false;
        }
    }

    return parsed?.[AUTO_GENERATED_META_KEY] === AUTO_GENERATED_META_VALUE;
}

/**
 * Whether an event satisfies a default-block entry. Title match only: a
 * generated shell is stamped with the entry's name as its title, and a
 * hand-made block titled like the entry stands in for one (CodeRabbit — a
 * generated "Morning" used to satisfy every same-type entry, so an
 * "Afternoon" default never got created).
 */
export function eventSatisfiesDefaultEntry(
    event: BlockMatchEvent,
    entry: DefaultBlockConfig,
): boolean {
    return event?.type === entry.blockType && event?.title === entry.name;
}

/**
 * Which config entries still need a block created for the given date?
 *
 * An entry is satisfied when an existing event of the same type exists that day
 * with the entry's title (eventSatisfiesDefaultEntry): a generated shell is
 * stamped with it, and a hand-made block titled like the entry counts too —
 * (b) covers blocks the user created manually — if a dispatcher already added
 * an "Ops Manager" block by hand, the default should not duplicate it.
 */
export function findMissingDefaultBlocks(
    entries: DefaultBlockConfig[],
    events: BlockMatchEvent[],
): DefaultBlockConfig[] {
    return entries.filter((entry) => {
        return !events.some((event) => eventSatisfiesDefaultEntry(event, entry));
    });
}

/**
 * Whether an event matches a default-block entry by the same semantics
 * findMissingDefaultBlocks uses to satisfy an entry (same type + title).
 * Used by the edit-block dialog's add/remove-as-default toggle.
 */
export function eventMatchesDefaultEntry(
    event: BlockMatchEvent,
    entry: DefaultBlockConfig,
): boolean {
    return event?.type === entry.blockType && event?.title === entry.name;
}

/** The config entry the event matches (by type + title), or null. */
export function findMatchingDefaultEntry(
    event: BlockMatchEvent,
    entries: DefaultBlockConfig[],
): DefaultBlockConfig | null {
    return entries.find((entry) => eventMatchesDefaultEntry(event, entry)) ?? null;
}

/** Local hour-of-day of a unix-seconds timestamp. */
function hourOfDayLocal(unixSeconds: number): number {
    return new Date(unixSeconds * 1000).getHours();
}

/**
 * Build a DefaultBlockConfig from an existing block event so it can be
 * appended to the calendarEvents.defaultBlocks config ("Add as default block").
 *
 * Hours are the event's local wall-clock hours; an end at or before the start
 * is stored as-is (endHour may be <= startHour after wrapping past 24) and
 * buildDefaultBlockInput re-derives the overnight span on the way back out.
 */
export function buildDefaultEntryFromEvent(
    event: BlockMatchEvent,
): DefaultBlockConfig | null {
    const blockType = event?.type;
    if (!isBlockType(blockType)) return null;

    const name = event?.title?.trim();
    if (!name) return null;

    if (typeof event.start !== 'number' || typeof event.end !== 'number') return null;

    const startHour = hourOfDayLocal(event.start);
    const endHour = hourOfDayLocal(event.end);

    return {
        blockType,
        name,
        startHour,
        endHour,
    };
}

/**
 * Build the createCalendarEvent input for a default block entry on a given date.
 * The client-supplied id makes the optimistic placeholder and the server event
 * share an id (the same trick the custom block dialog uses).
 */
export function buildDefaultBlockInput(
    entry: DefaultBlockConfig,
    date: Date,
): {
    eventId: string;
    type: string;
    title: string;
    description: string;
    start: number;
    end: number;
    crewRequirement?: BlockCrewRequirement;
    metadata: Record<string, string>;
} {
    const day = date && !Number.isNaN(date.getTime()) ? date : new Date();
    const start = dayStartUnix(day, entry.startHour);
    let end = dayStartUnix(day, entry.endHour);
    if (end <= start) {
        // End hour at or before start — treat as next-day (e.g. overnight 22→6).
        end += 24 * 60 * 60;
    }

    return {
        eventId: uuid(),
        type: entry.blockType,
        title: entry.name,
        description: entry.name,
        start,
        end,
        crewRequirement: entry.crewRequirement,
        metadata: {
            [AUTO_GENERATED_META_KEY]: AUTO_GENERATED_META_VALUE,
        },
    };
}

/** Unix seconds at the given hour on the given date's local day. */
function dayStartUnix(date: Date, hour: number): number {
    const d = new Date(date);
    d.setHours(hour, 0, 0, 0);
    return Math.floor(d.getTime() / 1000);
}